import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { Toast } from "./Toast";
const ToastContext = createContext(null);
const AUTO_DISMISS_MS = 4000;
let nextId = 0;
function makeId() {
    nextId += 1;
    return `toast-${nextId}`;
}
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const timersRef = useRef(new Map());
    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        const timer = timersRef.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(id);
        }
    }, []);
    const showToast = useCallback((input) => {
        const id = makeId();
        setToasts((prev) => [...prev, { ...input, id }]);
        const timer = setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
        timersRef.current.set(id, timer);
        return id;
    }, [dismiss]);
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
            timers.clear();
        };
    }, []);
    const value = useMemo(() => ({ showToast, dismiss }), [showToast, dismiss]);
    return (_jsxs(ToastContext.Provider, { value: value, children: [children, toasts.length > 0 && (_jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "false", style: {
                    position: "fixed",
                    top: "var(--hydrax-space-lg)",
                    right: "var(--hydrax-space-lg)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--hydrax-space-sm)",
                    zIndex: 1000,
                    pointerEvents: "none",
                }, children: toasts.map((t) => (_jsx("div", { style: { pointerEvents: "auto" }, children: _jsx(Toast, { id: t.id, tone: t.tone, message: t.message, onDismiss: dismiss }) }, t.id))) })), _jsx("style", { children: `
        @keyframes hydrax-toast-in {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      ` })] }));
}
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error("useToast must be used inside a <ToastProvider>");
    }
    return ctx;
}
//# sourceMappingURL=ToastProvider.js.map